"use client"

import { useEffect, useState } from "react"
import { useParams, Navigate } from "react-router-dom"
import NewDatasetForm from "@/components/datasets/NewDatasetForm"
import { useAuth } from "@/hooks/useAuth"
import { getDatasets, type Dataset } from "@/services/datasetsService"

/**
 * Edit Dataset page.
 *
 * Loads the dataset by ID from the URL params and passes it to the form
 * in edit mode.
 *
 * Only the dataset owner can access this page — redirects to the dataset
 * detail view if the current user is not the owner.
 */
export default function EditDatasetPage(): React.JSX.Element {
  const { datasetId } = useParams<{ datasetId: string }>()
  const { getAccessToken, user } = useAuth()

  const [dataset, setDataset] = useState<Dataset | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadDataset(): Promise<void> {
      if (!datasetId) {
        setError("No dataset ID provided")
        setLoading(false)
        return
      }

      try {
        const accessToken = await getAccessToken()
        const datasets = await getDatasets(accessToken)
        const found = datasets.find((d) => d.datasetId === datasetId)

        if (!found) {
          setError("Dataset not found")
        } else {
          setDataset(found)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load dataset")
      } finally {
        setLoading(false)
      }
    }

    loadDataset()
  }, [datasetId, getAccessToken])

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Loading dataset…</p>
      </div>
    )
  }

  // Error state
  if (error || !dataset) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-destructive">{error ?? "Dataset not found"}</p>
      </div>
    )
  }

  // Authorization check: only owner can edit
  if (user?.sub !== dataset.owner) {
    return <Navigate to={`/datasets/${datasetId}`} replace />
  }

  return (
    <>
      <title>Edit Dataset - MAP Apps Platform</title>
      <NewDatasetForm dataset={dataset} />
    </>
  )
}
